import { useParams, Link } from "react-router-dom";
import { Heart, Share2, BedDouble, Bath, Maximize, Calendar } from "lucide-react";
import { PROPERTIES } from "@/data";
import { useAuth } from "@/context/AuthContext";
import { PropertyCard } from "@/components/PropertyCard";
import { LeadDialog } from "@/components/LeadForm";
import { Reveal } from "@/components/Motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function estPayment(price) {
  const loan = price * 0.8, r = 0.065 / 12, n = 360;
  const pi = (loan * r) / (1 - Math.pow(1 + r, -n));
  return Math.round(pi + (price * 0.011) / 12 + 120);
}

export default function PropertyDetail() {
  const { id } = useParams();
  const p = PROPERTIES.find((x) => x.id === id);
  const { user, savedIds, toggleSave } = useAuth();
  if (!p) return <div className="pt-40 pb-40 text-center">Listing not found. <Link to="/search" className="text-gold underline">Back to search</Link></div>;
  const saved = savedIds.includes(p.id);
  const similar = PROPERTIES.filter((x) => x.id !== p.id && x.county === p.county).slice(0, 3);

  const onSave = async () => {
    if (!user) { toast.error("Please sign in to save homes."); return; }
    await toggleSave(p.id);
    toast.success(saved ? "Removed from saved homes" : "Saved to your homes");
  };
  const onShare = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied to clipboard"); };

  return (
    <div className="bg-cream pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-xs text-charcoal/50 mb-4 font-mono">SAMPLE LISTING · {p.id} · Not live MLS data</p>
        <div className="grid md:grid-cols-3 gap-3 mb-10">
          <img src={p.image} alt={p.address} className="md:col-span-2 rounded-2xl h-[420px] w-full object-cover" />
          <div className="grid grid-rows-2 gap-3">
            <img src={p.image} alt="" className="rounded-2xl h-full w-full object-cover" />
            <img src={p.image} alt="" className="rounded-2xl h-full w-full object-cover" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <Reveal>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="font-serif text-5xl text-navy">${p.price.toLocaleString()}</div>
                  <div className="text-charcoal/70 text-lg mt-1">{p.address}, {p.city}, GA {p.zip}</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={onSave} data-testid="detail-save" className="border border-navy/20 rounded-full px-5 h-11 inline-flex items-center gap-2 text-navy hover:bg-navy hover:text-cream transition-colors"><Heart size={16} className={saved ? "fill-gold text-gold" : ""} /> Save</button>
                  <button onClick={onShare} data-testid="detail-share" className="border border-navy/20 rounded-full px-5 h-11 inline-flex items-center gap-2 text-navy hover:bg-navy hover:text-cream transition-colors"><Share2 size={16} /> Share</button>
                </div>
              </div>
            </Reveal>
            <div className="flex gap-6 text-charcoal/70 font-mono border-y border-black/10 py-5">
              <span className="flex items-center gap-2"><BedDouble size={18} /> {p.beds} beds</span>
              <span className="flex items-center gap-2"><Bath size={18} /> {p.baths} baths</span>
              <span className="flex items-center gap-2"><Maximize size={18} /> {p.sqft.toLocaleString()} sf</span>
            </div>
            <div>
              <h2 className="font-serif text-2xl text-navy mb-3">About this home</h2>
              <p className="text-charcoal/75 leading-relaxed">{p.desc}</p>
            </div>
            <div>
              <h3 className="font-serif text-2xl text-navy mb-3">Property information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Type", p.type], ["Year built", p.year], ["Lot size", p.lot], ["Garage", `${p.garage} car`], ["HOA", p.hoa ? "Yes" : "No"], ["Status", p.status]].map(([k, v]) => (
                  <div key={k} className="flex justify-between bg-white rounded-xl px-4 py-3 border border-black/5"><span className="text-charcoal/50">{k}</span><span className="text-navy font-medium">{v}</span></div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl text-navy mb-3">Features</h3>
              <div className="flex flex-wrap gap-2">
                {p.features.map((ft) => <span key={ft} className="bg-white border border-black/5 rounded-full px-4 py-2 text-sm text-charcoal/75">{ft}</span>)}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl text-navy mb-3">Map</h3>
              <div className="rounded-2xl border border-black/10 h-56 bg-navy/5 flex items-center justify-center text-charcoal/40 font-mono text-sm" data-testid="map-placeholder">Map placeholder</div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="bg-white rounded-3xl p-7 border border-black/5 sticky top-28">
              <div className="text-sm text-charcoal/60 mb-1">Estimated monthly payment</div>
              <div className="font-serif text-3xl text-navy mb-1">${estPayment(p.price).toLocaleString()}/mo</div>
              <p className="text-xs text-charcoal/40 mb-6">Educational estimate only — not a loan offer or approval.</p>
              <div className="flex flex-col gap-3">
                <LeadDialog type="showing" source={`property-${p.id}-showing`} title="Request a Showing"
                  defaults={{ city: p.address }} successMsg="Your showing request has been received. Richard will contact you to confirm availability and appointment details."
                  extraFields={[
                    { name: "date", label: "Preferred date", type: "text", placeholder: "MM/DD/YYYY" },
                    { name: "time", label: "Preferred time", type: "text", placeholder: "e.g. 2:00 PM" },
                    { name: "tour", label: "Tour type", type: "select", options: ["In-person", "Video tour"] },
                    { name: "preapproved", label: "Preapproval status", type: "select", options: ["Pre-approved", "In progress", "Not yet"] },
                    { name: "other_agent", label: "Working with another agent?", type: "select", options: ["No", "Yes"] },
                  ]}
                  trigger={<Button data-testid="request-showing" className="bg-gold text-navy font-semibold rounded-full w-full h-12"><Calendar size={16} className="mr-1" /> Request a Showing</Button>} />
                <LeadDialog type="contact" source={`property-${p.id}-question`} title="Ask a Question"
                  extraFields={[{ name: "question", label: "Your question", type: "textarea", placeholder: "Ask about this home…" }]}
                  trigger={<Button data-testid="ask-question" className="bg-navy text-cream font-semibold rounded-full w-full h-12">Ask a Question</Button>} />
                <LeadDialog type="consultation" source={`property-${p.id}-consult`} title="Schedule a Consultation"
                  trigger={<Button variant="outline" data-testid="detail-consult" className="border-navy/20 text-navy font-semibold rounded-full w-full h-12">Schedule a Consultation</Button>} />
              </div>
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif text-3xl text-navy mb-8">Similar homes</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{similar.map((s) => <PropertyCard key={s.id} p={s} />)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
